/**
 * Invitations Service
 *
 * Handles space invitation system including creating, accepting,
 * declining, and managing invitations.
 */

import {
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { auth } from '../lib/firebase';
import { invitationsCollection } from '../lib/firestore-collections';
import type {
  SpaceInvitation,
  CreateInvitationInput,
} from '../types/firestore';
import { addMember } from './members.service';

/**
 * Create a new space invitation
 * Generates secure token and sets expiration (7 days)
 */
export async function createInvitation(
  input: CreateInvitationInput
): Promise<SpaceInvitation> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to create invitations');
  }

  // Generate secure random token
  const inviteToken = generateInviteToken();

  // Set expiration to 7 days from now
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 7);

  const invitationData = {
    spaceId: input.spaceId,
    spaceName: '', // Will be set by caller
    invitedEmail: input.invitedEmail.toLowerCase(),
    // Don't include invitedUserId - it will be set when user accepts (Firestore doesn't allow undefined)
    role: input.role,
    invitedBy: user.uid,
    invitedByName: input.invitedByName || user.displayName || user.email || 'Unknown',
    status: 'pending' as const,
    inviteToken,
    expiresAt: Timestamp.fromDate(expiresAt),
    createdAt: serverTimestamp(),
    emailSent: false, // Will be set to true when email is sent
  };

  const docRef = await addDoc(invitationsCollection, invitationData as any);

  return {
    ...invitationData,
    id: docRef.id,
    createdAt: Timestamp.now(),
  } as SpaceInvitation;
}

/**
 * Get an invitation by ID
 */
export async function getInvitation(
  invitationId: string
): Promise<SpaceInvitation | null> {
  const docRef = doc(invitationsCollection, invitationId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    // @ts-ignore
id: docSnap.id,
    ...docSnap.data(),
  } as SpaceInvitation;
}

/**
 * Get invitation by token
 * Used for accepting invitations via link
 */
export async function getInvitationByToken(
  token: string
): Promise<SpaceInvitation | null> {
  const q = query(
    invitationsCollection,
    where('inviteToken', '==', token),
    where('status', '==', 'pending')
  );

  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return null;
  }

  const docSnap = querySnapshot.docs[0];
  return {
    // @ts-ignore
id: docSnap.id,
    ...docSnap.data(),
  } as SpaceInvitation;
}

/**
 * Get all invitations for a space
 */
export async function getSpaceInvitations(
  spaceId: string
): Promise<SpaceInvitation[]> {
  const q = query(
    invitationsCollection,
    where('spaceId', '==', spaceId),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((docSnap) => ({
    // @ts-ignore
id: docSnap.id,
    ...docSnap.data(),
  })) as SpaceInvitation[];
}

/**
 * Get pending invitations for current user's email
 */
export async function getPendingInvitations(): Promise<SpaceInvitation[]> {
  const user = auth.currentUser;
  if (!user || !user.email) {
    return [];
  }

  const q = query(
    invitationsCollection,
    where('invitedEmail', '==', user.email.toLowerCase()),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  const invitations = querySnapshot.docs.map((docSnap) => ({
    // @ts-ignore
id: docSnap.id,
    ...docSnap.data(),
  })) as SpaceInvitation[];

  // Filter out expired invitations
  const now = Date.now();
  return invitations.filter((inv) => inv.expiresAt.toMillis() > now);
}

/**
 * Accept an invitation
 * Adds user as member to the space
 */
export async function acceptInvitation(invitationId: string): Promise<void> {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('User must be authenticated to accept invitations');
  }

  const invitation = await getInvitation(invitationId);
  if (!invitation) {
    throw new Error('Invitation not found');
  }

  // Check if invitation is valid
  if (invitation.status !== 'pending') {
    throw new Error('Invitation is no longer valid');
  }

  if (invitation.expiresAt.toMillis() < Date.now()) {
    throw new Error('Invitation has expired');
  }

  if (invitation.invitedEmail.toLowerCase() !== user.email.toLowerCase()) {
    throw new Error('Invitation is for a different email address');
  }

  // Add user as member to the space
  await addMember(
    invitation.spaceId,
    user.uid,
    user.email,
    invitation.role,
    invitation.invitedBy,
    user.displayName || undefined,
    user.photoURL || undefined
  );

  // Update invitation status
  const docRef = doc(invitationsCollection, invitationId);
  await updateDoc(docRef, {
    status: 'accepted',
    invitedUserId: user.uid,
    respondedAt: serverTimestamp(),
  });
}

/**
 * Decline an invitation
 */
export async function declineInvitation(invitationId: string): Promise<void> {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('User must be authenticated');
  }

  const invitation = await getInvitation(invitationId);
  if (!invitation) {
    throw new Error('Invitation not found');
  }

  if (invitation.invitedEmail.toLowerCase() !== user.email.toLowerCase()) {
    throw new Error('Invitation is for a different email address');
  }

  const docRef = doc(invitationsCollection, invitationId);
  await updateDoc(docRef, {
    status: 'declined',
    respondedAt: serverTimestamp(),
  });
}

/**
 * Cancel an invitation
 * Only the inviter or space owner can cancel
 */
export async function cancelInvitation(invitationId: string): Promise<void> {
  const docRef = doc(invitationsCollection, invitationId);
  await updateDoc(docRef, {
    status: 'cancelled',
    respondedAt: serverTimestamp(),
  });
}

/**
 * Delete an invitation
 * Only owner can delete
 */
export async function deleteInvitation(invitationId: string): Promise<void> {
  const docRef = doc(invitationsCollection, invitationId);
  await deleteDoc(docRef);
}

/**
 * Mark invitation email as sent
 */
export async function markEmailSent(invitationId: string): Promise<void> {
  const docRef = doc(invitationsCollection, invitationId);
  await updateDoc(docRef, {
    emailSent: true,
    emailSentAt: serverTimestamp(),
  });
}

/**
 * Expire old invitations
 * Should be run periodically (could be a cloud function in production)
 */
export async function expireOldInvitations(): Promise<void> {
  const q = query(
    invitationsCollection,
    where('status', '==', 'pending'),
    orderBy('expiresAt', 'asc')
  );

  const querySnapshot = await getDocs(q);
  const now = Date.now();

  const expiredInvitations = querySnapshot.docs.filter(
    (doc) => doc.data().expiresAt.toMillis() < now
  );

  // Update expired invitations
  const updatePromises = expiredInvitations.map((docSnap) => {
    const docRef = doc(invitationsCollection, docSnap.id);
    return updateDoc(docRef, {
      status: 'expired',
    });
  });

  await Promise.all(updatePromises);
}

/**
 * Generate a secure random invitation token
 */
function generateInviteToken(): string {
  // Generate cryptographically secure random token
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate invitation link
 */
export function generateInvitationLink(token: string): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/invite/${token}`;
}

/**
 * Check if user has already been invited to a space
 */
export async function hasExistingInvitation(
  email: string,
  spaceId: string
): Promise<boolean> {
  const q = query(
    invitationsCollection,
    where('invitedEmail', '==', email.toLowerCase()),
    where('spaceId', '==', spaceId),
    where('status', '==', 'pending')
  );

  const querySnapshot = await getDocs(q);
  return !querySnapshot.empty;
}

/**
 * Resend invitation email via Cloud Function
 * Triggers the resendInvitationEmail Cloud Function to send another email
 */
export async function resendInvitationEmail(
  invitationId: string
): Promise<{ success: boolean; messageId: string; invitedEmail: string }> {
  const cloudFunctionUrl =
    'https://us-central1-nidonote.cloudfunctions.net/resendInvitationEmail';

  const response = await fetch(cloudFunctionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ invitationId }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to resend invitation: ${errorText}`);
  }

  return await response.json();
}
